import React from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { createPortal } from 'react-dom'
import {
  trackDepositLimitPopUpDisplayed,
  trackEditDepositLimit,
  trackKeepDepositLimit,
  trackNeedAssistance,
} from '@mobi/account/Common/Analytics'
import { useHostContext } from '@mobi/account/Common/HostContext'
import { useAccountHolders, useBetAccount, useHistory } from '@mobi/account/Hooks'
import { BamAccountHolder } from '@mobi/api-types'
import { FeatureIcon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { ModalOrTray } from '@mobi/component-library/Overlays/ModalOrTray'
import { toTitleCase } from '@mobi/utils/string'
import {
  DepositLimitAndAnniversaryRequest,
  useSaveDepositLimitAndAnniversary,
} from '../Hooks/useSaveDepositLimitAndAnniversary'
import { routes } from '../Utils/routes'

type Props = { accountNumber: number | null }
export const DepositLimitsAnniversary = ({ accountNumber }: Props) => {
  const [isErrorDismissed, setIsErrorDismissed] = React.useState(false)
  const isLoggedIn = !!accountNumber
  const appHistory = useHistory()
  const { channel } = useHostContext()
  const isDesktop = channel === 'desktop'

  const { data: betAccount, refetch: refetchBetAccount } = useBetAccount({
    enabled: isLoggedIn,
  })

  const { saveDepositLimitAndAnniversary, saveDepositLimitAndAnniversaryStatus } =
    useSaveDepositLimitAndAnniversary()
  const accountHolderWithUpcomingAnniversary = getAccountHolderWithUpcomingAnniversary(
    betAccount?.AccountHolderList
  )

  const shouldShowDialog =
    isLoggedIn &&
    accountHolderWithUpcomingAnniversary !== null &&
    saveDepositLimitAndAnniversaryStatus !== 'error'

  const { data: accountHolders } = useAccountHolders({
    enabled: shouldShowDialog,
  })

  const {
    ProposedOptOutDate,
    MaxBetAmount,
    ProposedMaxBetAmount,
    ProposedMaxBetDate,
    OptInMarketingIndicator,
    UserCreditCardBlockedIndicator,
    ProposedUserCreditCardBlockedDate,
    BlockPayPalIndicator,
    ProposedPayPalReleaseDate,
    MaxDepositAmount,
    MaxDepositFrequency,
  } = accountHolders?.primaryAccountHolder?.PreCommitmentDetails ?? {}

  const {
    FullName: fullName,
    PreCommitmentMaxDepositAmount: limitAmount,
    PreCommitmentMaxDepositFrequency,
    AccountHolderId: accountHolderId,
  } = accountHolderWithUpcomingAnniversary || {}

  const limitFrequencyLabel = PreCommitmentMaxDepositFrequency
    ? toTitleCase(PreCommitmentMaxDepositFrequency)
    : ''

  React.useEffect(() => {
    if (shouldShowDialog) {
      trackDepositLimitPopUpDisplayed()
    }
  }, [shouldShowDialog])

  if (!accountHolderId || !accountNumber) return null

  const onSubmit = (keep: boolean) => {
    const newMaxBetAmount =
      ProposedMaxBetDate || ProposedOptOutDate ? ProposedMaxBetAmount : MaxBetAmount
    const preCommitmentDetails: DepositLimitAndAnniversaryRequest = {
      betAccountNumber: accountNumber,
      optInMarketingIndicator: OptInMarketingIndicator ?? false,
      preCommitmentMaxDepositAmount: MaxDepositAmount ?? undefined,
      preCommitmentMaxDepositFrequency: MaxDepositFrequency ?? undefined,
      maxBetAmount: newMaxBetAmount,
      userCreditCardBlockedIndicator:
        !ProposedUserCreditCardBlockedDate && UserCreditCardBlockedIndicator,
      blockPayPalIndicator: !ProposedPayPalReleaseDate && BlockPayPalIndicator,
    }

    saveDepositLimitAndAnniversary(preCommitmentDetails, {
      onSuccess: () => {
        refetchBetAccount()
        if (!keep) {
          if (channel === 'desktop') {
            window.location.href = routes.editDepositLimit
          } else {
            appHistory.push(routes.editDepositLimit)
          }
        }
      },
    })
  }

  const ErrorTray = (
    <ModalOrTray
      isModal={isDesktop}
      isOpen={saveDepositLimitAndAnniversaryStatus === 'error' && !isErrorDismissed}
    >
      <TrayContent style={{ textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <FeatureIcon type='error' size='xl' />
        </div>
        <Header>Deposit Limit Error</Header>
        <Text>
          Please try again later on the Deposit Limit page or call{' '}
          <CustomerServiceTelephone
            isInternational={false}
            onClick={() => trackNeedAssistance('Deposit Limit')}
          />
          .
        </Text>
        <Button
          color='primary'
          onClick={() => {
            setIsErrorDismissed(true)
          }}
        >
          Close
        </Button>
      </TrayContent>
    </ModalOrTray>
  )

  const Content = (
    <>
      {ErrorTray}
      <ModalOrTray isModal={isDesktop} isOpen={shouldShowDialog} headerTitle='Deposit Limit'>
        <TrayContent>
          <Text>
            Hi <b>{fullName}</b>, to continue betting, please review your deposit limit.
          </Text>
          <InfoBox color='blue' size='md'>
            {!limitAmount ? (
              <div>No deposit limit set</div>
            ) : (
              <div>
                Currently set to{' '}
                <b>
                  ${limitAmount} {limitFrequencyLabel}
                </b>
              </div>
            )}
          </InfoBox>
          <InfoBox color='grey' hideIcon={true} size='md'>
            At TABtouch we support responsible wagering. To help you manage your betting, this is
            your annual reminder to review your deposit limit. You can also review this at any time
            by visiting the Deposit Limit page in My Account menu.
          </InfoBox>
          <ButtonsContainer>
            <Button
              color='primary'
              isFullWidth
              disabled={saveDepositLimitAndAnniversaryStatus === 'pending'}
              onClick={() => {
                trackKeepDepositLimit()
                onSubmit(true)
              }}
            >
              Keep Deposit Limit
            </Button>
            <Button
              color='secondary_grey'
              isFullWidth
              disabled={saveDepositLimitAndAnniversaryStatus === 'pending'}
              onClick={() => {
                trackEditDepositLimit()
                onSubmit(false)
              }}
            >
              Change
            </Button>
          </ButtonsContainer>
        </TrayContent>
      </ModalOrTray>
    </>
  )

  return createPortal(Content, document.body)
}

const DAYS_BEFORE_ANNIVERSARY_REMINDER = 14

const getAccountHolderWithUpcomingAnniversary = (accountHolders?: BamAccountHolder[]) => {
  if (!accountHolders || accountHolders.length === 0) return null

  const isInDateRange = (anniversary: Date | null) =>
    !!(
      anniversary &&
      dayjs(anniversary).diff(dayjs(), 'days', true) <= DAYS_BEFORE_ANNIVERSARY_REMINDER
    )

  const primaryAccountHolder = accountHolders?.find(ah => ah.IsPrimaryAccountHolder)
  if (!primaryAccountHolder) return null

  const primaryAccountHolderIsInDateRange = isInDateRange(
    primaryAccountHolder.PreCommitmentAnniversaryDate
  )

  if (primaryAccountHolderIsInDateRange) {
    return primaryAccountHolder
  }

  return null
}

const Text = styled.span({
  color: colors.neutral[900],
  ...font.size.lg,
})

const TrayContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
})

const ButtonsContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.smx1,
})

const Header = styled.div({
  display: 'flex',
  justifyContent: 'center',
  fontSize: font.size.xl2.fontSize,
  fontWeight: font.weight.semibold,
  paddingTop: spacing.smx2,
})

export default DepositLimitsAnniversary
